// src/components/admin/EditEvent.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../../providers/dataProvider";
import EventCard from "../events/EventCard";
import EventDetails from "../events/EventDetails";
import ImageSelector from "../shared/ImageSelector";
import { pageMeta } from "../../config/helmetConfig";
import { updateMetaTags } from "../../utils/metaManager";

// Main Edit Event Component
export default function EditEvent() {
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    updateMetaTags(pageMeta.admin);
  }, []);
  // Use the consolidated DataContext with the additional image methods
  const {
    events,
    eventsLoading,
    updateEvent,
    addEvent,
    images,
    getImageUrl,
    getImageByKey,
  } = useData();

  const [newEvent, setEvent] = useState({
    id: "",
    month: "",
    day: "",
    year: new Date().getFullYear(),
    time: "",
    title: "",
    location: "The Style Emporium",
    address: "5041 N Academy Blvd, Colorado Springs, CO 80918, USA",
    short_description: "",
    description: "",
    image_path: "", // This holds the aws_location
  });

  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const saveDisabled =
    saving ||
    saveSuccess ||
    !newEvent.title ||
    !newEvent.month ||
    !newEvent.day ||
    !newEvent.time ||
    !newEvent.short_description ||
    !newEvent.description;

  useEffect(() => {
    // Only attempt to find the event when loading is complete
    if (id === "new") {
      return;
    }

    if (!eventsLoading && events.length > 0) {
      const foundEvent = events.find(
        (event) => event.id.toString() === id.toString()
      );

      if (foundEvent) {
        // Get the aws_location from the image_id
        const image = images.find((img) => img.id === foundEvent.image_id);
        const imagePath = image ? image.aws_location : "";

        // Update state with the found event data
        setEvent({
          id: foundEvent.id,
          month: foundEvent.month,
          day: foundEvent.day,
          year: foundEvent.year,
          time: foundEvent.time,
          title: foundEvent.title,
          location: foundEvent.location,
          address: foundEvent.address,
          short_description:
            foundEvent.short_description || foundEvent.description,
          description: foundEvent.about || foundEvent.description,
          image_path: imagePath, // Store the aws_location in image_path for the UI
        });
      }
    }
  }, [events, eventsLoading, id, images]);

  // Update preview image URL when image_path changes
  useEffect(() => {
    const updatePreviewUrl = async () => {
      if (newEvent.image_path) {
        try {
          const url = await getImageUrl(newEvent.image_path);
          setPreviewImageUrl(url);
        } catch (error) {
          console.error("Error getting image URL:", error);
          setPreviewImageUrl(null);
        }
      } else {
        setPreviewImageUrl(null);
      }
    };

    updatePreviewUrl();
  }, [newEvent.image_path, getImageUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEvent({
      ...newEvent,
      [name]: value,
    });
  };

  const handleImageChange = (imageKey) => {
    setEvent({
      ...newEvent,
      image_path: imageKey,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      // Get the image ID from the path
      const image = getImageByKey(newEvent.image_path);
      const imageId = image ? image.id : null;

      // Create the data object for the API with the correct field names
      const eventData = {
        month: newEvent.month,
        day: newEvent.day,
        year: newEvent.year,
        time: newEvent.time,
        title: newEvent.title,
        location: newEvent.location,
        address: newEvent.address,
        short_description: newEvent.short_description,
        description: newEvent.description,
        image_id: imageId, // This is what the backend expects
      };

      if (id === "new") {
        await addEvent(eventData);
      } else {
        await updateEvent(id, eventData);
      }
    } catch (error) {
      console.error("Error updating event:", error);
    } finally {
      setSaving(false);
      setSaveSuccess(true);
      setTimeout(() => {
        navigate("/admin/events");
      }, 2000);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <h1 className="text-light">{id === "new" ? "Add" : "Edit"} Event</h1>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate("/admin/events")}
          >
            Back to Events
          </Button>
        </Col>
      </Row>
      {saveSuccess && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success">
              Event updated successfully! Redirecting...
            </Alert>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          {/* Edit Form Section */}
          <Col md={7} className="mb-4">
            <Card
              className="bg-dark text-white border-secondary"
              style={{ width: "100%" }}
            >
              <Card.Header className="bg-dark text-white">
                Event Details
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Event Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={newEvent.title}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Month (3 letters)</Form.Label>
                      <Form.Control
                        type="text"
                        name="month"
                        placeholder="JAN"
                        maxLength={3}
                        value={newEvent.month}
                        onChange={handleInputChange}
                        required
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Day</Form.Label>
                      <Form.Control
                        type="text"
                        name="day"
                        placeholder="01"
                        maxLength={2}
                        value={newEvent.day}
                        onChange={handleInputChange}
                        required
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Year</Form.Label>
                      <Form.Control
                        type="number"
                        name="year"
                        value={newEvent.year}
                        onChange={handleInputChange}
                        required
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Time</Form.Label>
                  <Form.Control
                    type="text"
                    name="time"
                    placeholder="4:00 PM - 6:00 PM"
                    value={newEvent.time}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={newEvent.location}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={newEvent.address}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Short Description</Form.Label>
                  <Form.Control
                    type="text"
                    name="short_description"
                    value={newEvent.short_description}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                  <Form.Text className="text-muted">
                    Brief description for event cards
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Full Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="description"
                    value={newEvent.description}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                {/* Replace the old file upload with the ImageSelector component */}
                <ImageSelector
                  label="Event Image"
                  value={newEvent.image_path}
                  onChange={handleImageChange}
                />

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/admin/events")}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={saveDisabled}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Preview Section */}
          <EventCard
            event={{
              ...newEvent,
              // Pass the preview URL to the EventCard component
              image: previewImageUrl,
            }}
          />
          <EventDetails
            preview
            event={{
              ...newEvent,
              image: previewImageUrl,
            }}
          />
        </Row>
      </Form>
    </Container>
  );
}
